import React, { useEffect, useId, useMemo, useState } from 'react';
import { getReversalHistory } from '../api/brapi';
import { formatTradingDate } from '../utils/dates';

const CHART = {
  width: 860,
  padLeft: 48,
  padRight: 150,
  priceTop: 18,
  priceHeight: 210,
  volumeTop: 238,
  volumeHeight: 48,
  rsiTop: 318,
  rsiHeight: 104,
  height: 442,
};

const COLORS = {
  grid: '#21303d',
  muted: '#718696',
  candleUp: '#36c79f',
  candleDown: '#ed6c68',
  ema: '#55b7e8',
  sma: '#a78bfa',
  rsi: '#d4a7ff',
  entry: '#f2bd59',
  stop: '#ed6c68',
  target: '#49c99d',
  reference: '#f2bd59',
};

const MIN_REQUIRED_HISTORY_BARS = 200;

function finite(value) {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
}

function positiveFinite(value) {
  return finite(value) && Number(value) > 0;
}

function formatPrice(value) {
  if (!finite(value)) return '—';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value) {
  return formatTradingDate(value);
}

function dateKey(value) {
  return value ? String(value).slice(0, 10) : null;
}

function historyWindowLabel(history, count) {
  if (count === 120) return '120 pregões · 6 meses';
  if (count === 250) return '250 pregões · 1 ano';
  if (count === 500) return '500 pregões · 2 anos';
  const first = new Date(`${dateKey(history?.firstDate || history?.bars?.[0]?.date)}T12:00:00Z`);
  const last = new Date(`${dateKey(history?.lastDate || history?.bars?.at(-1)?.date)}T12:00:00Z`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return `${count} candles`;
  const years = Math.floor((last - first) / (365.25 * 24 * 60 * 60 * 1000));
  if (years >= 1) return `${years} ${years === 1 ? 'ano' : 'anos'} · ${count} candles`;
  const months = Math.max(1, Math.floor((last - first) / (30.44 * 24 * 60 * 60 * 1000)));
  return `${months} ${months === 1 ? 'mês' : 'meses'} · ${count} candles`;
}

function buildPath(values, xAt, yAt) {
  let path = '';
  let drawing = false;
  values.forEach((value, index) => {
    if (!finite(value)) {
      drawing = false;
      return;
    }
    path += `${drawing ? ' L' : 'M'} ${xAt(index).toFixed(1)},${yAt(Number(value)).toFixed(1)}`;
    drawing = true;
  });
  return path;
}

export function resolveRulerLabels(items, minY, maxY, gap = 14) {
  if (!items.length) return [];
  const sorted = [...items].sort((left, right) => left.desiredY - right.desiredY);
  const resolved = [];
  sorted.forEach(item => {
    const previous = resolved.at(-1);
    resolved.push({
      ...item,
      y: previous
        ? Math.max(item.desiredY, previous.y + gap)
        : Math.max(minY, item.desiredY),
    });
  });

  const overflow = resolved.at(-1).y - maxY;
  if (overflow > 0) resolved.forEach(item => { item.y -= overflow; });
  for (let index = resolved.length - 2; index >= 0; index -= 1) {
    resolved[index].y = Math.min(resolved[index].y, resolved[index + 1].y - gap);
  }
  const underflow = minY - resolved[0].y;
  if (underflow > 0) resolved.forEach(item => { item.y += underflow; });
  return resolved;
}

function ReversalChart({ history, setup }) {
  const historyBars = useMemo(() => history?.bars || [], [history]);
  const [windowSize, setWindowSize] = useState(120);
  const priceClipId = `reversal-price-clip-${useId().replace(/:/g, '')}`;
  const visibleWindowSize = Math.min(windowSize, historyBars.length);
  const bars = useMemo(() => historyBars.slice(-visibleWindowSize), [historyBars, visibleWindowSize]);
  useEffect(() => {
    if (windowSize > historyBars.length) setWindowSize(120);
  }, [historyBars.length, windowSize]);
  if (historyBars.length < MIN_REQUIRED_HISTORY_BARS) {
    return (
      <div className="rm-chart-state" data-testid="status-reversal-chart-insufficient">
        <strong>Dados insuficientes</strong>
        <span>A BRAPI não retornou pelo menos 200 candles, necessários para exibir a SMA200 com segurança.</span>
      </div>
    );
  }

  const n = bars.length;
  const innerWidth = CHART.width - CHART.padLeft - CHART.padRight;
  const step = n > 1 ? innerWidth / (n - 1) : innerWidth;
  const candleHalf = Math.max(1.7, Math.min(5, step * 0.32));
  const xAt = index => CHART.padLeft + (n > 1 ? (index / (n - 1)) * innerWidth : innerWidth / 2);

  const levelValues = [setup?.entryPrice, setup?.stopPrice, setup?.target1Price]
    .filter(finite)
    .map(Number);
  const highValues = bars.map(bar => Number(bar.high)).filter(Number.isFinite).concat(levelValues);
  const lowValues = bars.map(bar => Number(bar.low)).filter(Number.isFinite).concat(levelValues);
  const pMax = Math.max(...highValues) * 1.012;
  const pMin = Math.min(...lowValues) * 0.988;
  const pRange = pMax - pMin || 1;
  const priceY = value => CHART.priceTop + ((pMax - value) / pRange) * CHART.priceHeight;
  const maxVolume = Math.max(...bars.map(bar => positiveFinite(bar.volume) ? Number(bar.volume) : null).filter(Number.isFinite), 1);
  const rsiY = value => CHART.rsiTop + ((100 - Math.max(0, Math.min(100, value))) / 100) * CHART.rsiHeight;

  const requestedReferenceDate = dateKey(setup?.referenceDate);
  const foundReferenceIndex = requestedReferenceDate
    ? bars.findIndex(bar => dateKey(bar.date) === requestedReferenceDate)
    : -1;
  const referenceAvailable = Boolean(requestedReferenceDate && foundReferenceIndex >= 0);
  const referenceDate = requestedReferenceDate;
  const referenceIndex = foundReferenceIndex >= 0 ? foundReferenceIndex : n - 1;
  const referenceBar = referenceAvailable ? bars[referenceIndex] : null;
  const referenceLabel = 'CANDLE REF.';
  const referenceColor = setup?.status === 'DISPARADO' ? COLORS.target : COLORS.reference;
  const referenceStatus = !requestedReferenceDate
    ? 'CANDLE DE REFERÊNCIA NÃO INFORMADO'
    : referenceAvailable
      ? 'CANDLE DE REFERÊNCIA'
      : 'CANDLE DE REFERÊNCIA NÃO ENCONTRADO';
  const priceGrid = [0, 0.33, 0.67, 1].map(t => pMin + (1 - t) * pRange);
  const xLabels = bars.map((bar, index) => {
    if (index !== n - 1 && index % Math.max(1, Math.ceil(n / 7)) !== 0) return null;
    return (
      <text key={`date-${bar.date}`} x={xAt(index)} y={CHART.height - 8} fill={index === n - 1 ? '#d7b768' : COLORS.muted} fontSize="8" textAnchor="middle" fontFamily="'JetBrains Mono', monospace">
        {formatDate(bar.date)}
      </text>
    );
  });

  return (
    <div className="rm-chart" data-testid="reversal-setup-chart">
      <div className="rm-chart-head">
        <div>
          <strong>HISTÓRICO TÉCNICO · {history.ticker}</strong>
          <span>
            Último pregão: {formatDate(history.lastDate)}
            {(history.adjustmentSource || history.source) && (
              <> · {history.priceAdjusted ? 'Série ajustada · ' : ''}Fonte: {history.adjustmentSource || history.source}</>
            )}
          </span>
        </div>
        <label className="rm-chart-window">
            <span>{historyWindowLabel(history, visibleWindowSize)}</span>
          <select data-testid="select-reversal-window" aria-label="Janela do gráfico" value={windowSize} onChange={event => setWindowSize(Number(event.target.value))}>
            {[120, 250, 500].map(value => <option key={value} value={value} disabled={value > historyBars.length}>{value}</option>)}
          </select>
        </label>
      </div>
      <div className="rm-chart-canvas">
        <svg viewBox={`0 0 ${CHART.width} ${CHART.height}`} role="img" aria-label={`Gráfico de candles, médias, volume e IFR(2) de ${history.ticker}`}>
          <defs>
            <clipPath id={priceClipId}>
              <rect x={CHART.padLeft} y={CHART.priceTop} width={innerWidth} height={CHART.priceHeight} rx="2" />
            </clipPath>
          </defs>
          <rect clipPath={`url(#${priceClipId})`} x={CHART.padLeft} y={CHART.priceTop} width={innerWidth} height={CHART.priceHeight} fill="rgba(255,255,255,0.012)" rx="2" />
          {priceGrid.map((value, index) => (
            <g key={`grid-${index}`}>
              <line clipPath={`url(#${priceClipId})`} x1={CHART.padLeft} x2={CHART.width - CHART.padRight} y1={priceY(value)} y2={priceY(value)} stroke={COLORS.grid} strokeWidth="0.8" />
              <text x={CHART.padLeft - 5} y={priceY(value) + 3} fill={COLORS.muted} fontSize="8" textAnchor="end" fontFamily="'JetBrains Mono', monospace">{Number(value).toFixed(0)}</text>
            </g>
          ))}

          <text x={CHART.padLeft + 5} y={CHART.priceTop + 11} fill="#91a6b6" fontSize="8" fontWeight="700" fontFamily="'JetBrains Mono', monospace">PREÇO</text>
          <path clipPath={`url(#${priceClipId})`} d={buildPath(bars.map(bar => bar.ema21), xAt, priceY)} fill="none" stroke={COLORS.ema} strokeWidth="1.5" />
          <path clipPath={`url(#${priceClipId})`} d={buildPath(bars.map(bar => bar.sma200), xAt, priceY)} fill="none" stroke={COLORS.sma} strokeWidth="1.4" strokeDasharray="5 4" />

          {bars.map((bar, index) => {
            const isUp = Number(bar.close) >= Number(bar.open);
            const color = isUp ? COLORS.candleUp : COLORS.candleDown;
            const x = xAt(index);
            const bodyTop = priceY(Math.max(Number(bar.open), Number(bar.close)));
            const bodyBottom = priceY(Math.min(Number(bar.open), Number(bar.close)));
            return (
              <g clipPath={`url(#${priceClipId})`} key={bar.date}>
                <title>{`${formatDate(bar.date)} · O ${formatPrice(bar.open)} · H ${formatPrice(bar.high)} · L ${formatPrice(bar.low)} · C ${formatPrice(bar.close)}${bar.needsReview ? ' · REVISAR: possível efeito de provento/data ex' : ''}`}</title>
                {bar.needsReview && <rect x={x - candleHalf - 2} y={CHART.priceTop} width={candleHalf * 2 + 4} height={CHART.priceHeight} fill={COLORS.entry} opacity="0.12" />}
                <line x1={x} x2={x} y1={priceY(Number(bar.high))} y2={priceY(Number(bar.low))} stroke={color} strokeWidth="0.9" />
                <rect x={x - candleHalf} y={bodyTop} width={candleHalf * 2} height={Math.max(1, bodyBottom - bodyTop)} fill={color} />
              </g>
            );
          })}

          {finite(referenceBar?.high) && (
               <g data-testid="reversal-reference-marker">
              <rect x={xAt(referenceIndex) - candleHalf - 2} y={CHART.priceTop} width={candleHalf * 2 + 4} height={CHART.priceHeight} fill={referenceColor} opacity="0.1" />
               <line clipPath={`url(#${priceClipId})`} x1={xAt(referenceIndex)} x2={xAt(referenceIndex)} y1={CHART.priceTop} y2={CHART.rsiTop + CHART.rsiHeight} stroke={referenceColor} strokeDasharray="2 4" opacity="0.65" />
              <circle cx={xAt(referenceIndex)} cy={priceY(Number(referenceBar.high)) - 4} r="3" fill={referenceColor} />
              <rect x={Math.min(Math.max(xAt(referenceIndex) - 32, CHART.padLeft), CHART.width - CHART.padRight - 64)} y={CHART.priceTop + 3} width="64" height="14" rx="2" fill={referenceColor} />
              <text x={Math.min(Math.max(xAt(referenceIndex), CHART.padLeft + 32), CHART.width - CHART.padRight - 32)} y={CHART.priceTop + 13} textAnchor="middle" fill="#071016" fontSize="7.5" fontWeight="700" fontFamily="'JetBrains Mono', monospace">{referenceLabel}</text>
            </g>
          )}

          {(() => {
            const rawRuler = [
              ['E', 'Entrada', setup?.entryPrice, COLORS.entry],
              ['S', 'Stop', setup?.stopPrice, COLORS.stop],
              ['A1', 'Alvo 1', setup?.target1Price, COLORS.target],
              ['MME21', 'MME21', bars.at(-1)?.ema21, COLORS.ema],
              ['SMA200', 'SMA200', bars.at(-1)?.sma200, COLORS.sma],
            ].filter(row => finite(row[2]));
            const resolved = resolveRulerLabels(
              rawRuler.map(row => ({ row, desiredY: priceY(Number(row[2])) })),
              CHART.priceTop + 8,
              CHART.priceTop + CHART.priceHeight - 8,
            );
            return resolved.map(({ row, y }) => (
              <g key={`ruler-${row[0]}`} data-testid={`ruler-reversal-${row[0].toLowerCase()}`}>
                <line clipPath={`url(#${priceClipId})`} x1={CHART.padLeft} x2={CHART.width - CHART.padRight} y1={priceY(Number(row[2]))} y2={priceY(Number(row[2]))} stroke={row[3]} strokeWidth="1.1" strokeDasharray="5 4" opacity="0.72" />
                <line x1={CHART.width - CHART.padRight} x2={CHART.width - CHART.padRight + 7} y1={priceY(Number(row[2]))} y2={y} stroke={row[3]} strokeWidth="0.8" />
                <text x={CHART.width - CHART.padRight + 11} y={y + 3} fill={row[3]} fontSize="8.5" fontWeight="700" fontFamily="'JetBrains Mono', monospace">
                  {row[0]} {Number(row[2]).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </text>
              </g>
            ));
          })()}

          <line x1={CHART.padLeft} x2={CHART.width - CHART.padRight} y1={CHART.volumeTop - 8} y2={CHART.volumeTop - 8} stroke={COLORS.grid} />
          <text x={CHART.padLeft + 5} y={CHART.volumeTop + 2} fill={COLORS.muted} fontSize="8" fontWeight="700" fontFamily="'JetBrains Mono', monospace">VOLUME</text>
          {bars.map((bar, index) => {
            const volume = positiveFinite(bar.volume) ? Number(bar.volume) : null;
            const isUp = Number(bar.close) >= Number(bar.open);
            const barHeight = Number.isFinite(volume) ? (volume / maxVolume) * CHART.volumeHeight : 0;
            return (
              Number.isFinite(volume)
                ? <rect data-testid="volume-bar" key={`volume-${bar.date}`} x={xAt(index) - candleHalf} y={CHART.volumeTop + CHART.volumeHeight - barHeight} width={candleHalf * 2} height={Math.max(1, barHeight)} fill={isUp ? COLORS.candleUp : COLORS.candleDown} opacity="0.55" />
                : <line data-testid="volume-missing" key={`volume-missing-${bar.date}`} x1={xAt(index)} x2={xAt(index)} y1={CHART.volumeTop + 8} y2={CHART.volumeTop + CHART.volumeHeight - 1} stroke={COLORS.muted} strokeWidth="1.2" strokeDasharray="2 3" />
            );
          })}

          <rect x={CHART.padLeft} y={CHART.rsiTop} width={innerWidth} height={CHART.rsiHeight} fill="rgba(167,139,250,0.025)" rx="2" />
          <text x={CHART.padLeft + 5} y={CHART.rsiTop + 11} fill="#91a6b6" fontSize="8" fontWeight="700" fontFamily="'JetBrains Mono', monospace">IFR(2)</text>
          {[75, 50, 25].map(value => (
            <g key={`rsi-${value}`}>
              <line x1={CHART.padLeft} x2={CHART.width - CHART.padRight} y1={rsiY(value)} y2={rsiY(value)} stroke={value === 25 ? COLORS.entry : COLORS.grid} strokeWidth={value === 25 ? 1.1 : 0.7} strokeDasharray={value === 25 ? '5 3' : '3 5'} opacity={value === 25 ? 0.95 : 0.8} />
              <text x={CHART.padLeft - 5} y={rsiY(value) + 3} fill={value === 25 ? COLORS.entry : COLORS.muted} fontSize="8" textAnchor="end" fontFamily="'JetBrains Mono', monospace">{value}</text>
            </g>
          ))}
          <path d={buildPath(bars.map(bar => bar.rsi2), xAt, rsiY)} fill="none" stroke={COLORS.rsi} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          {finite(referenceBar?.rsi2) && (
            <g>
              <circle cx={xAt(referenceIndex)} cy={rsiY(Number(referenceBar.rsi2))} r="4" fill={referenceColor} stroke="#101923" strokeWidth="1.5" />
              <text x={xAt(referenceIndex) + 7} y={rsiY(Number(referenceBar.rsi2)) + 3} fill={referenceColor} fontSize="7.5" fontWeight="700" fontFamily="'JetBrains Mono', monospace">REF {Number(referenceBar.rsi2).toFixed(1)}</text>
            </g>
          )}
          {xLabels}
        </svg>
      </div>
      <div className="rm-chart-legend" aria-label="Legenda do gráfico">
        <span><i className="ema" /> MME21</span>
        <span><i className="sma" /> SMA200</span>
        <span><i className="rsi" /> IFR(2)</span>
        {finite(setup?.entryPrice) && <span><i className="entry" /> Entrada</span>}
        {finite(setup?.stopPrice) && <span><i className="stop" /> Stop</span>}
        {finite(setup?.target1Price) && <span><i className="target" /> Alvo 1</span>}
        {bars.some(bar => bar.needsReview) && <span><i className="review" /> Revisar data ex/provento (faixa)</span>}
        {bars.some(bar => !positiveFinite(bar.volume)) && <span><i className="missing" /> Volume ausente (linha pontilhada)</span>}
        {referenceAvailable && <span><i className="reference" /> Candle de referência</span>}
      </div>
      <div className="rm-chart-reference">
        <strong className={!referenceAvailable ? 'unavailable' : ''}>
          {setup?.status ? `${setup.status} · ` : ''}{referenceStatus} · {formatDate(referenceDate)}
        </strong>
        <span>
          {!requestedReferenceDate
            ? 'O setup não informou uma data de referência; o gráfico não inventa uma marcação.'
            : !referenceAvailable
              ? 'A data do setup não está presente no histórico retornado pela BRAPI; o gráfico não inventa uma marcação.'
              : setup?.trigger || 'Candidato: aguardando um gatilho válido de IFR(2) ou pullback na MME21.'}
        </span>
      </div>
    </div>
  );
}

export default function ReversalSetupChart({ ticker, setup }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [insufficient, setInsufficient] = useState(false);

  useEffect(() => {
    let active = true;
    if (!ticker) {
      setHistory(null);
      setError(null);
      setInsufficient(false);
      return () => { active = false; };
    }
    setLoading(true);
    setError(null);
    setInsufficient(false);
    setHistory(null);
    getReversalHistory(ticker)
      .then(result => {
        if (!active) return;
        if (!result?.bars || result.bars.length < MIN_REQUIRED_HISTORY_BARS) {
          setInsufficient(true);
          return;
        }
        setHistory(result);
      })
      .catch(err => {
        if (!active) return;
        if (err?.response?.status === 422) {
          setInsufficient(true);
        } else {
          setError(err?.response?.data?.error || err.message || 'Não foi possível carregar o histórico pela BRAPI.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [ticker]);

  if (loading) {
    return <div className="rm-chart-state" data-testid="status-reversal-chart-loading"><span className="rm-loader" />Carregando histórico técnico pela BRAPI…</div>;
  }
  if (insufficient) {
    return <div className="rm-chart-state" data-testid="status-reversal-chart-insufficient"><strong>Dados insuficientes</strong><span>A BRAPI não retornou pelo menos 200 candles, necessários para exibir a SMA200 com segurança.</span></div>;
  }
  if (error) {
    return <div className="rm-chart-state error" data-testid="status-reversal-chart-error"><strong>Histórico indisponível</strong><span>{error}</span></div>;
  }
  if (!history) return null;
  return <ReversalChart history={history} setup={setup} />;
}